import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDataStore } from '../stores/dataStore'
import { 
  Shield, 
  MapPin, 
  Clock, 
  AlertTriangle, 
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  User,
  Activity,
  X
} from 'lucide-react'
import AttachmentViewer from '../components/common/AttachmentViewer'
import './GuardOperations.css'

// Simple Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'medium' }) => {
  if (!isOpen) return null
  
  const sizeClass = size === 'large' ? 'modal-large' : size === 'small' ? 'modal-small' : 'modal-medium'
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-container ${sizeClass}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  )
}

// Form Input Component
const FormInput = ({ label, type = 'text', name, value, onChange, required = false, options = null }) => {
  if (type === 'select' && options) {
    return (
      <div className="form-group">
        <label>{label}{required && <span className="required">*</span>}</label>
        <select name={name} value={value} onChange={onChange} required={required} className="form-control">
          <option value="">Select {label}</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    )
  }
  
  if (type === 'textarea') {
    return (
      <div className="form-group">
        <label>{label}{required && <span className="required">*</span>}</label>
        <textarea name={name} value={value} onChange={onChange} required={required} className="form-control" rows="3" />
      </div>
    )
  }
  
  return (
    <div className="form-group">
      <label>{label}{required && <span className="required">*</span>}</label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} className="form-control" />
    </div>
  )
}

export default function GuardOperations() {
  const [activeTab, setActiveTab] = useState('monitoring')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Modal states
  const [showGuardModal, setShowGuardModal] = useState(false)
  const [showShiftModal, setShowShiftModal] = useState(false)
  const [showIncidentModal, setShowIncidentModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [editMode, setEditMode] = useState(false)
  
  const { 
    guards, 
    shifts, 
    incidents,
    sites,
    liveMonitoring,
    fetchGuards,
    fetchShifts, 
    fetchIncidents,
    fetchSites,
    fetchLiveMonitoring,
    createGuard,
    updateGuard,
    deleteGuard,
    createShift,
    updateShift,
    deleteShift,
    createIncident,
    updateIncident,
    deleteIncident,
    createSite,
    updateSite,
    deleteSite,
    isLoading 
  } = useDataStore()

  const navigate = useNavigate()

  useEffect(() => {
    fetchGuards()
    fetchShifts()
    fetchIncidents()
    fetchSites()
    fetchLiveMonitoring()
    const interval = setInterval(fetchLiveMonitoring, 30000)
    return () => clearInterval(interval)
  }, [])

  // Form states
  const [guardForm, setGuardForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reportedBy: '',
    department: '',
    position: '',
    status: 'Active'
  })

  const [shiftForm, setShiftForm] = useState({
    reportedBy: '',
    guardName: '',
    site: '',
    siteId: '',
    date: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    shiftType: 'Day',
    notes: ''
  })

  const [incidentForm, setIncidentForm] = useState({
    title: '',
    description: '',
    type: 'Security',
    severity: 'Medium',
    siteId: '',
    reportedBy: '',
    createdBy: '',
    attachments: []
  })

  // New Site inline form state
  const [showNewSiteForm, setShowNewSiteForm] = useState(false)
  const [newSiteForm, setNewSiteForm] = useState({
    name: '',
    site_name: '',
    code: '',
    clientName: '',
    type: 'commercial',
    address: { street: '', city: '', county: '' },
    guardsRequired: 1,
    status: 'active'
  })

  // Filter functions
  const filteredGuards = guards.filter(g => 
    g.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.guardId?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredShifts = shifts.filter(s => {
    const siteName = (typeof s.site === 'string' ? s.site : s.site?.name) || '';
    const guardName = s.guardsAssigned?.[0]?.guard?.firstName || s.guardName || '';
    return siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           guardName.toLowerCase().includes(searchTerm.toLowerCase());
  })

  const handleGuardSubmit = async (e) => {
    e.preventDefault()
    const result = editMode 
      ? await updateGuard(selectedItem._id, guardForm)
      : await createGuard(guardForm)
    
    if (result.success) {
      setShowGuardModal(false)
      resetGuardForm()
      fetchGuards()
    } else {
      alert('Error: ' + result.error)
    }
  }

  const createSiteFromShift = async () => {
    if (!newSiteForm.name || !newSiteForm.code) {
      alert('Site name and code are required')
      return null
    }
    try {
      const token = localStorage.getItem('token')
      const res = await fetch('https://www.secs.gismac.co.ke/api/sites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newSiteForm,
          name: newSiteForm.name,
          site_name: newSiteForm.name,
          clientName: newSiteForm.clientName || '',
          client_name: newSiteForm.clientName || ''
        })
      })
      const data = await res.json()
      if (data.success) {
        await fetchSites()
        setShowNewSiteForm(false)
        setNewSiteForm({
          name: '', site_name: '', code: '', clientName: '', type: 'commercial',
          address: { street: '', city: '', county: '' }, guardsRequired: 1, status: 'active'
        })
        return data.data
      } else {
        alert('Error creating site: ' + (data.message || 'Unknown error'))
        return null
      }
    } catch (err) {
      alert('Exception creating site: ' + err.message)
      return null
    }
  }

  const handleShiftSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      ...shiftForm,
      site: shiftForm.site || shiftForm.siteId || '',
      guardName: shiftForm.guardName,
      guardsAssigned: shiftForm.guardId ? [{
        guard: shiftForm.guardId,
        guard_id: shiftForm.guardId,
        guardName: shiftForm.guardName
      }] : []
    }
    const result = editMode
      ? await updateShift(selectedItem._id, payload)
      : await createShift(payload)
    
    if (result.success) {
      setShowGuardModal(false)
      resetGuardForm()
      fetchGuards()
    } else {
      alert('Error: ' + result.error)
    }
  }

  const handleIncidentSubmit = async (e) => {
    e.preventDefault()
    
    const data = new FormData()
    data.append('title', incidentForm.title || '')
    data.append('description', incidentForm.description || '')
    data.append('type', incidentForm.type || 'Security')
    data.append('severity', incidentForm.severity || 'Medium')
    data.append('siteId', incidentForm.siteId || '')
    data.append('reportedBy', incidentForm.reportedBy || '')
    data.append('createdByName', incidentForm.createdBy || '')    

    // Attachment must be LAST in FormData for multer to parse text fields first
     if (incidentForm.attachments && incidentForm.attachments.length > 0) {
        incidentForm.attachments.forEach(file => data.append('attachments', file))
      }

    
    const result = editMode
      ? await updateIncident(selectedItem._id, data)
      : await createIncident(data)
    
    if (result.success) {
      setShowIncidentModal(false)
      resetIncidentForm()
      fetchIncidents()
    } else {
      alert('Error: ' + result.error)
    }
  }

  const handleDelete = async (type, id) => {
    if (!confirm('Are you sure you want to delete this ' + type + '?')) return
    
    let result
    try {
      switch(type) {
        case 'guard':
          result = await deleteGuard(id)
          if (result.success) fetchGuards()
          break
        case 'shift':
          result = await deleteShift(id)
          if (result.success) fetchShifts()
          break
        case 'incident':
          result = await deleteIncident(id)
          if (result.success) fetchIncidents()
          break
        default:
          return
      }
      
      if (!result || !result.success) {
        alert('Error deleting ' + type + ': ' + (result?.error || 'Unknown error'))
      } else {
        alert(type + ' deleted successfully!')
      }
    } catch (err) {
      alert('Exception deleting ' + type + ': ' + err.message)
    }
  }

  const handleEdit = (type, item) => {
    setSelectedItem(item)
    setEditMode(true)
    
    switch(type) {
      case 'guard':
        setGuardForm({
          firstName: item.firstName || '',
          lastName: item.lastName || '',
          email: item.email || '',
          phone: item.phone || '',
          guardId: item.guardId || '',
          department: item.department || '',
          position: item.position || '',
          status: item.status || 'Active'
        })
        setShowGuardModal(true)
        break
      case 'shift':
        const assignedGuard = item.guardsAssigned?.[0];
        setShiftForm({
          guardId: assignedGuard?.guard || assignedGuard?._id || '',
          guardName: assignedGuard?.guard || '',
          site: item.site || '',
          siteId: item.siteId || item.site_id || item.site?._id || '',
          date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
          startDate: item.startDate ? new Date(item.startDate).toISOString().split('T')[0] : '',
          endDate: item.endDate ? new Date(item.endDate).toISOString().split('T')[0] : '',
          startTime: item.startTime || '',
          endTime: item.endTime || '',
          shiftType: item.shiftType || 'Day',
          notes: item.notes || ''
        })
        setShowShiftModal(true)
        break
      case 'incident':
        setIncidentForm({
          title: item.title || '',
          description: item.description || '',
          type: item.type || 'Security',
          severity: item.severity || 'Medium',
          siteId: item.site?._id || '',
          guardId: item.reportedBy?._id || ''
        })
        setShowIncidentModal(true)
        break
    }
  }

  const handleView = (item) => {
    setSelectedItem(item)
    setShowViewModal(true)
  }

  const resetGuardForm = () => {
    setGuardForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      reportedBy: '',
      department: '',
      position: '',
      status: 'Active'
    })
    setEditMode(false)
    setSelectedItem(null)
  }

  const resetShiftForm = () => {
    setShiftForm({
      reportedBy: '',
      siteId: '',
      date: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      shiftType: 'Day',
      notes: ''
    })
    setEditMode(false)
    setSelectedItem(null)
  }

  const resetIncidentForm = () => {
    setIncidentForm({
      title: '',
      description: '',
      type: 'Security',
      severity: 'Medium',
      siteId: '',
      reportedBy: '',
      attachments: []
    })
    setEditMode(false)
    setSelectedItem(null)
  }

  const openAddModal = (type) => {
    setEditMode(false)
    setSelectedItem(null)
    switch(type) {
      case 'guard':
        resetGuardForm()
        setShowGuardModal(true)
        break
      case 'shift':
        resetShiftForm()
        setShowShiftModal(true)
        break
      case 'incident':
        resetIncidentForm()
        setShowIncidentModal(true)
        break
    }
  }

  return (
    <div className="guard-operations">
      <div className="page-header">
        <div className="header-content">
          <h1><Shield className="icon" /> Guard Operations</h1>
          <p>Manage guards, shifts, and incidents</p>
        </div>
      </div>

      <div className="module-nav">
        <button 
          className={`tab ${activeTab === 'monitoring' ? 'active' : ''}`}
          onClick={() => setActiveTab('monitoring')}
        >
          <Activity size={18} /> Live Monitoring
        </button>
        <button 
          className={`tab ${activeTab === 'shifts' ? 'active' : ''}`}
          onClick={() => setActiveTab('shifts')}
        >
          <Clock size={18} /> Shift Schedule
        </button>
        <button 
          className={`tab ${activeTab === 'incidents' ? 'active' : ''}`}
          onClick={() => setActiveTab('incidents')}
        >
          <AlertTriangle size={18} /> Incidents
        </button>
        <button 
          className={`tab ${activeTab === 'guards' ? 'active' : ''}`}
          onClick={() => setActiveTab('guards')}
        >
          <Shield size={18} /> All Guards
        </button>
      </div>

      <div className="tab-content">
        {/* Live Monitoring Tab */}
        {activeTab === 'monitoring' && (
          <div className="monitoring-section">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon on-duty">
                  <Shield size={24} />
                </div>
                <div className="stat-info">
                  <h3>{liveMonitoring?.onDutyGuards || 0}</h3>
                  <p>Guards On Duty</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon off-duty">
                  <Clock size={24} />
                </div>
                <div className="stat-info">
                  <h3>{liveMonitoring?.offDutyGuards || 0}</h3>
                  <p>Guards Off Duty</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon incidents">
                  <AlertTriangle size={24} />
                </div>
                <div className="stat-info">
                  <h3>{liveMonitoring?.activeIncidents || 0}</h3>
                  <p>Active Incidents</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon sites">
                  <MapPin size={24} />
                </div>
                <div className="stat-info">
                  <h3>{liveMonitoring?.activeSites || 0}</h3>
                  <p>Active Sites</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shifts Tab */}
        {activeTab === 'shifts' && (
          <div className="shifts-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input 
                  type="text" 
                  placeholder="Search shifts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={() => openAddModal('shift')}>
                <Plus size={18} />
                Create Shift
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Site / Location</th>
                    <th>Guard Name</th>
                    <th>Time</th>
                    <th>Shift Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShifts.map(shift => (
                    <tr key={shift._id}>
                      <td>{shift.date ? new Date(shift.date).toLocaleDateString() : '-'}</td>
                      <td>{typeof shift.site === 'string' ? shift.site : shift.site?.name || '-'}</td>
                      <td>{(() => {
                        const guardId = shift.guardsAssigned?.[0]?.guard || shift.guardId;
                        if (!guardId) return shift.guardName || '-';
                        const guard = guards.find(g => (g._id || g.id) === guardId);
                        return guard ? `${guard.firstName || ''} ${guard.lastName || ''}`.trim() : (shift.guardName || guardId || '-');
                      })()}</td>
                      <td>{shift.startTime || '-'} - {shift.endTime || '-'}</td>
                      <td>{shift.shiftType || 'Day'}</td>
                      <td>
                        <span className={`badge badge-${shift.status || 'scheduled'}`}>
                          {shift.status || 'Scheduled'}
                        </span>
                      </td>
                      <td>
                        <button className="btn-icon" onClick={() => handleView(shift)}><Eye size={16} /></button>
                        <button className="btn-icon" onClick={() => handleEdit('shift', shift)}><Edit size={16} /></button>
                        <button className="btn-icon" onClick={() => handleDelete('shift', shift._id)}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <div className="incidents-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search incidents..." />
              </div>
              <button className="btn btn-primary" onClick={() => openAddModal('incident')}>
                <Plus size={18} />
                Report Incident
              </button>
            </div>
                          <div className="incidents-grid">
                {incidents.map(incident => (
                  <div key={incident._id} className={`incident-card ${incident.severity}`}>
                    <div className="incident-header">
                      <span className="incident-id">{incident.incidentId}</span>
                      <span className={`severity-badge ${incident.severity}`}>
                        {incident.severity}
                      </span>
                    </div>
                          {incident.attachments && incident.attachments.map((url, idx) => (
                      <a key={idx} href={url} target="_blank">File {idx + 1}</a>
))}                            
                          
                                          
                    <h4>{incident.type?.replace('_', ' ').toUpperCase()}</h4>
                    <p className="incident-description">{incident.description}</p>
                    <div className="incident-meta">
  <span><User size={14} /> Created By: {incident.createdByName || incident.createdBy || 'N/A'}</span>
</div>
                    <div className="incident-footer">
                      <span className="incident-date">{incident.createdAt ? new Date(incident.createdAt).toLocaleDateString() : '-'}</span>
                      <div className="incident-actions">
                        <button className="btn-icon" onClick={() => handleView(incident)}><Eye size={16} /></button>
                        <button className="btn-icon" onClick={() => handleEdit('incident', incident)}><Edit size={16} /></button>
                        <button className="btn-icon" onClick={() => handleDelete('incident', incident._id)}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

         

       

        {/* Guards Tab */}
        {activeTab === 'guards' && (
          <div className="guards-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input 
                  type="text" 
                  placeholder="Search guards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={() => openAddModal('guard')}>
                <Plus size={18} />
                Add Guard
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Guard ID</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuards.map(guard => (
                    <tr key={guard._id}>
                      <td>{guard.guardId || '-'}</td>
                      <td>{guard.firstName || ''} {guard.lastName || ''}</td>
                      <td>{guard.department || '-'}</td>
                      <td>{guard.position || '-'}</td>
                      <td>
                        <span className={`badge badge-${guard.status?.toLowerCase() || 'active'}`}>
                          {guard.status || 'Active'}
                        </span>
                      </td>
                      <td>
                        <button className="btn-icon" onClick={() => handleView(guard)}><Eye size={16} /></button>
                        <button className="btn-icon" onClick={() => handleEdit('guard', guard)}><Edit size={16} /></button>
                        <button className="btn-icon" onClick={() => handleDelete('guard', guard._id)}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Guard Modal */}
      <Modal 
        isOpen={showGuardModal} 
        onClose={() => setShowGuardModal(false)}
        title={editMode ? 'Edit Guard' : 'Add New Guard'}
        size="large"
      >
        <form onSubmit={handleGuardSubmit} className="modal-form">
          <div className="form-row">
            <FormInput 
              label="First Name" 
              name="firstName" 
              value={guardForm.firstName} 
              onChange={(e) => setGuardForm({...guardForm, firstName: e.target.value})}
              required 
            />
            <FormInput 
              label="Last Name" 
              name="lastName" 
              value={guardForm.lastName} 
              onChange={(e) => setGuardForm({...guardForm, lastName: e.target.value})}
              required 
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Email" 
              type="email"
              name="email" 
              value={guardForm.email} 
              onChange={(e) => setGuardForm({...guardForm, email: e.target.value})}
            />
            <FormInput 
              label="Phone" 
              type="tel"
              name="phone" 
              value={guardForm.phone} 
              onChange={(e) => setGuardForm({...guardForm, phone: e.target.value})}
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Guard ID" 
              name="reportedBy" 
              value={guardForm.guardId} 
              onChange={(e) => setGuardForm({...guardForm, guardId: e.target.value})}
              required 
            />
            <FormInput 
              label="Department" 
              name="department" 
              value={guardForm.department} 
              onChange={(e) => setGuardForm({...guardForm, department: e.target.value})}
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Position" 
              name="position" 
              value={guardForm.position} 
              onChange={(e) => setGuardForm({...guardForm, position: e.target.value})}
            />
            <FormInput 
              label="Status" 
              type="select"
              name="status" 
              value={guardForm.status} 
              onChange={(e) => setGuardForm({...guardForm, status: e.target.value})}
              options={[
                {value: 'Active', label: 'Active'},
                {value: 'On Leave', label: 'On Leave'},
                {value: 'Suspended', label: 'Suspended'},
                {value: 'Terminated', label: 'Terminated'}
              ]}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => setShowGuardModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (editMode ? 'Update' : 'Add Guard')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Shift Modal */}
      <Modal 
        isOpen={showShiftModal} 
        onClose={() => setShowShiftModal(false)}
        title={editMode ? 'Edit Shift' : 'Create New Shift'}
        size="large"
      >
        <form onSubmit={handleShiftSubmit} className="modal-form">
          <div className="form-row">
            <FormInput 
              label="Guard" 
              type="select"
              name="reportedBy" 
              value={shiftForm.guardId} 
              onChange={(e) => {
                const guard = guards.find(g => g._id === e.target.value)
                setShiftForm({
                  ...shiftForm, 
                  guardId: e.target.value,
                  guardName: guard ? `${guard.firstName} ${guard.lastName}` : ''
                })
              }}
              options={guards.map(g => ({value: g._id, label: `${g.firstName} ${g.lastName}`}))}
            />
            <div className="site-select-wrapper" style={{position: 'relative'}}>
              <FormInput 
                label="Site" 
                type="select"
                name="siteId" 
                value={shiftForm.siteId} 
                onChange={(e) => {
                  if (e.target.value === '__NEW_SITE__') {
                    setShowNewSiteForm(true)
                    return
                  }
                  const site = sites.find(s => s._id === e.target.value)
                  setShiftForm({
                    ...shiftForm, 
                    siteId: e.target.value,
                    site: site ? site.name : ''
                  })
                }}
                options={[
                  ...sites.map(s => ({value: s._id, label: s.name})),
                  {value: '__NEW_SITE__', label: '+ Add New Site'}
                ]}
              />
              
              {showNewSiteForm && (
                <div className="new-site-inline" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  marginTop: '8px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                    <h4 style={{margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#111827'}}>Add New Site</h4>
                    <button 
                      type="button" 
                      onClick={() => setShowNewSiteForm(false)}
                      style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280'}}
                    >X</button>
                  </div>
                  <div className="form-row">
                    <FormInput 
                      label="Site Name *" 
                      name="newSiteName" 
                      value={newSiteForm.name} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, name: e.target.value, site_name: e.target.value})}
                      required
                    />
                    <FormInput 
                      label="Site Code *" 
                      name="newSiteCode" 
                      value={newSiteForm.code} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, code: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <FormInput 
                      label="Client Name" 
                      name="newSiteClient" 
                      value={newSiteForm.clientName} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, clientName: e.target.value})}
                    />
                    <FormInput 
                      label="Type" 
                      type="select"
                      name="newSiteType" 
                      value={newSiteForm.type} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, type: e.target.value})}
                      options={[
                        {value: 'commercial', label: 'Commercial'},
                        {value: 'residential', label: 'Residential'},
                        {value: 'industrial', label: 'Industrial'},
                        {value: 'government', label: 'Government'},
                        {value: 'educational', label: 'Educational'},
                        {value: 'healthcare', label: 'Healthcare'},
                        {value: 'retail', label: 'Retail'},
                        {value: 'other', label: 'Other'}
                      ]}
                    />
                  </div>
                  <div className="form-row">
                    <FormInput 
                      label="City" 
                      name="newSiteCity" 
                      value={newSiteForm.address.city} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, address: {...newSiteForm.address, city: e.target.value}})}
                    />
                    <FormInput 
                      label="County" 
                      name="newSiteCounty" 
                      value={newSiteForm.address.county} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, address: {...newSiteForm.address, county: e.target.value}})}
                    />
                  </div>
                  <div className="form-row">
                    <FormInput 
                      label="Guards Required" 
                      type="number"
                      name="newSiteGuards" 
                      value={newSiteForm.guardsRequired} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, guardsRequired: parseInt(e.target.value) || 1})}
                      min="1"
                    />
                    <FormInput 
                      label="Status" 
                      type="select"
                      name="newSiteStatus" 
                      value={newSiteForm.status} 
                      onChange={(e) => setNewSiteForm({...newSiteForm, status: e.target.value})}
                      options={[
                        {value: 'active', label: 'Active'},
                        {value: 'inactive', label: 'Inactive'},
                        {value: 'pending', label: 'Pending'}
                      ]}
                    />
                  </div>
                  <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
                    <button 
                      type="button" 
                      className="btn btn-outline" 
                      onClick={() => setShowNewSiteForm(false)}
                      style={{padding: '8px 16px', fontSize: '0.875rem'}}
                    >Cancel</button>
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={async () => {
                        const newSite = await createSiteFromShift()
                        if (newSite && newSite._id) {
                          setShiftForm({
                            ...shiftForm,
                            siteId: newSite._id,
                            site: newSite.name || newSite.site_name || ''
                          })
                        }
                      }}
                      style={{padding: '8px 16px', fontSize: '0.875rem'}}
                    >Create Site</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="form-row">
            <FormInput 
              label="Date" 
              type="date"
              name="date" 
              value={shiftForm.date} 
              onChange={(e) => setShiftForm({...shiftForm, date: e.target.value})}
            />
            <FormInput 
              label="Start Date" 
              type="date"
              name="startDate" 
              value={shiftForm.startDate} 
              onChange={(e) => setShiftForm({...shiftForm, startDate: e.target.value})}
            />
            <FormInput 
              label="End Date" 
              type="date"
              name="endDate" 
              value={shiftForm.endDate} 
              onChange={(e) => setShiftForm({...shiftForm, endDate: e.target.value})}
            />
            <FormInput 
              label="Shift Type" 
              type="select"
              name="shiftType" 
              value={shiftForm.shiftType} 
              onChange={(e) => setShiftForm({...shiftForm, shiftType: e.target.value})}
              options={[
                {value: 'Day', label: 'Day'},
                {value: 'Night', label: 'Night'},
                {value: 'Weekend', label: 'Weekend'},
                {value: 'Holiday', label: 'Holiday'},
                {value: 'Weekly', label: 'Weekly'}
              ]}
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Start Time" 
              type="time"
              name="startTime" 
              value={shiftForm.startTime} 
              onChange={(e) => setShiftForm({...shiftForm, startTime: e.target.value})}
            />
            <FormInput 
              label="End Time" 
              type="time"
              name="endTime" 
              value={shiftForm.endTime} 
              onChange={(e) => setShiftForm({...shiftForm, endTime: e.target.value})}
            />
          </div>
          <FormInput 
            label="Notes" 
            type="textarea"
            name="notes" 
            value={shiftForm.notes} 
            onChange={(e) => setShiftForm({...shiftForm, notes: e.target.value})}
          />
          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => setShowShiftModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (editMode ? 'Update' : 'Create Shift')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Incident Modal */}
      <Modal 
        isOpen={showIncidentModal} 
        onClose={() => setShowIncidentModal(false)}
        title={editMode ? 'Edit Incident' : 'Report New Incident'}
        size="large"
      >
        <form onSubmit={handleIncidentSubmit} className="modal-form">
          <div className="form-row">
            <FormInput 
              label="Title" 
              name="title" 
              value={incidentForm.title} 
              onChange={(e) => setIncidentForm({...incidentForm, title: e.target.value})}
              required 
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Type" 
              type="select"
              name="type" 
              value={incidentForm.type} 
              onChange={(e) => setIncidentForm({...incidentForm, type: e.target.value})}
              options={[
                {value: 'Security', label: 'Security'},
                {value: 'Safety', label: 'Safety'},
                {value: 'Medical', label: 'Medical'},
                {value: 'Fire', label: 'Fire'},
                {value: 'Theft', label: 'Theft'},
                {value: 'Vandalism', label: 'Vandalism'},
                {value: 'Death', label: 'Death'},
                {value: 'Assault', label: 'Assault'},
                {value: 'Bribery', label: 'Bribery'},
                {value: 'Other', label: 'Other'}
              ]}
            />
            <FormInput 
              label="Severity" 
              type="select"
              name="severity" 
              value={incidentForm.severity} 
              onChange={(e) => setIncidentForm({...incidentForm, severity: e.target.value})}
              options={[
                {value: 'Low', label: 'Low'},
                {value: 'Medium', label: 'Medium'},
                {value: 'High', label: 'High'},
                {value: 'Critical', label: 'Critical'}
              ]}
            />
          </div>
          <div className="form-row">
            <FormInput 
              label="Incident Location" 
              name="siteId" 
              value={incidentForm.siteId} 
              onChange={(e) => setIncidentForm({...incidentForm, siteId: e.target.value})}
            />
            <FormInput 
              label="Reported By" 
              type="select"
              name="reportedBy" 
              value={incidentForm.reportedBy} 
              onChange={(e) => setIncidentForm({...incidentForm, reportedBy: e.target.value})}
              options={[
                {value: 'Guard', label: 'Guard'},
                {value: 'Supervisor', label: 'Supervisor'},
                {value: 'Client', label: 'Client'},
                {value: 'Passerby', label: 'Passerby'},
                {value: 'HR', label: 'HR'},
                {value: 'Admin', label: 'Admin'},
                {value: 'Others', label: 'Others'}
              ]}
            />
          </div>
          <div className="form-group">
  <label>Created By</label>
  <input 
    type="text" 
    value={incidentForm.createdBy}
    onChange={(e) => setIncidentForm({...incidentForm, createdBy: e.target.value})}
    placeholder="Enter your name/ID"
  />
</div>
          <FormInput 
            label="Description" 
            type="textarea"
            name="description" 
            value={incidentForm.description} 
            onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
            required 
          />
          <div className="form-group">
            <label>Attachment (Photo/Document)</label>
            <input 
              type="file" 
              multiple
              name="attachment" 
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => setIncidentForm({...incidentForm, attachments: Array.from(e.target.files)})}
            />
            {incidentForm.attachment && (
              <p className="file-selected">Selected: {incidentForm.attachment.name}</p>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => setShowIncidentModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (editMode ? 'Update' : 'Report')}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal 
        isOpen={showViewModal} 
        onClose={() => setShowViewModal(false)}
        title="View Details"
        size="medium"
      >
        {selectedItem && (
          <div className="view-details">
            <pre style={{background: '#f3f4f6', padding: '16px', borderRadius: '8px', overflow: 'auto'}}>
              {JSON.stringify(selectedItem, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  )
}
